// ./src/containers/pages/app/training/content-quiz.jsx

import pdfSupport from 'browser-pdf-support';
import classNames from 'classnames';
import he from 'he';
import fp from 'lodash/fp';
import queryString from 'query-string';
import React from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import Spinner from 'react-spinkit';
import {
  branch,
  lifecycle,
  renderComponent,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators, compose } from 'redux';
import {
  adsBlockList,
  contentDisplayView,
  cpdPointStatus,
  routes,
  tabKeys
} from '../../../../constants';
import { transcriptSupportedContentTypes } from '../../../../constants/content-type';
import { getUserIndustryCpdCategorys } from '../../../../services';
import { maxQuizAttemptsSelector } from '../../../../state/modules/configuration';
import { contentActions } from '../../../../state/modules/content';
import {
  cpdPointActions,
  cpdPointsSelector
} from '../../../../state/modules/cpd-points';
import { publisherActions } from '../../../../state/modules/publisher';
import {
  currentAttemptSelector,
  quizActions
} from '../../../../state/modules/quiz';
import { taskActions } from '../../../../state/modules/task';
import { currentUserSelector } from '../../../../state/modules/user/selectors';
import { pluralise, toSafeExternalUrl } from '../../../../utils';
import { ControlGroup, ControlOption, Icon } from '../../../components';
import Loading from '../../loading';
import QuizQuestion from './partials/quiz-question';

const CONTENT_LOADING_TIMEOUT_IN_SECONDS = 5 * 1000;

/**
 * Default transcript pane width as a fraction of the split container width.
 * Example: 0.5 means transcript takes 50% of the available width.
 */
const DEFAULT_TRANSCRIPT_PANE_RATIO = 0.5;

/**
 * Smallest allowed transcript pane width as a fraction of the split container width.
 * This prevents the transcript pane from becoming too narrow to use.
 */
const MIN_TRANSCRIPT_PANE_RATIO = 0.25;

/**
 * Largest allowed transcript pane width as a fraction of the split container width.
 * This prevents the transcript pane from taking over the full content area.
 */
const MAX_TRANSCRIPT_PANE_RATIO = 0.75;

/**
 * Minimum transcript pane width in pixels.
 * Used to compute a ratio floor based on the current container width.
 */
const MIN_TRANSCRIPT_PANE_PX = 320;

/**
 * Restricts a numeric value to stay within a specified range.
 *
 * If `value` is lower than `min`, `min` is returned.
 * If `value` is higher than `max`, `max` is returned.
 * Otherwise, the original value is returned.
 *
 * @param {number} value - The number to clamp.
 * @param {number} min - The minimum allowed value.
 * @param {number} max - The maximum allowed value.
 * @returns {number} The clamped value.
 */
const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const EarnNowText = ({ onEarnMicroCpdPoints }) => (
  <p>
    Earn CPD for this Professional Reading
    <Button onClick={onEarnMicroCpdPoints}>
      Get points now <Icon icon='chevron-right-large' />
    </Button>
  </p>
);

const SetupText = () => (
  <p>
    Set up your CPD preferences to earn CPD for completing this quiz
    <LinkContainer
      to={routes.app.trainingPlansSettings(
        tabKeys.trainingPlansSettings.userTrainingPlan
      )}
    >
      <Button>Set up your CPD preferences</Button>
    </LinkContainer>
  </p>
);

const QuizAttemptText = ({
  quizAttempts,
  remainingAttempts,
  onStartQuizAttempt
}) => (
  <p>
    Answer questions about this content to earn CPD
    {quizAttempts.length > 0
      ? ` (${remainingAttempts} ${pluralise('attempt', remainingAttempts)} remaining)`
      : null}
    <Button onClick={onStartQuizAttempt}>
      Take quiz <Icon icon='chevron-right-large' />
    </Button>
  </p>
);

const CpdEarnedText = () => (
  <p>You've already earned CPD for this Professional Reading</p>
);

const CpdPointStatus = compose(
  branch(
    ({
      selectedContent,
      isFetchingCpdPoint,
      isFetchingQuizAttempts,
      quizAttempts
    }) =>
      !(
        selectedContent &&
        !isFetchingCpdPoint &&
        !isFetchingQuizAttempts &&
        quizAttempts !== null
      ),
    renderComponent(Loading)
  ),
  branch(
    ({ cpdPoint }) => cpdPoint && cpdPoint.status === cpdPointStatus.approved,
    renderComponent(CpdEarnedText)
  ),
  branch(
    ({ currentUser: { activated } }) => !activated,
    renderComponent(({ selectedContent: { contentType } }) => (
      <p>
        Your account is currently inactive. To earn CPD for this{' '}
        {fp.lowerCase(contentType)}
        <LinkContainer
          to={routes.app.accountSettings(tabKeys.accountSettings.paymentDetail)}
        >
          <Button>Reactivate your account</Button>
        </LinkContainer>
      </p>
    ))
  ),
  branch(
    ({ userSelectedCpdCategory, selectedContent }) =>
      !userSelectedCpdCategory && !fp.isEmpty(selectedContent.cpdCategorys),
    renderComponent(SetupText)
  ),
  branch(
    ({ quizQuestions }) => fp.isEmpty(quizQuestions),
    renderComponent(EarnNowText)
  ),
  branch(
    ({ quizAttempts, maxQuizAttempts, isUserTasked }) =>
      !isUserTasked && quizAttempts.length >= maxQuizAttempts,
    renderComponent(() => <p>No attempts remaining</p>)
  ),
  withProps(({ maxQuizAttempts, isUserTasked, quizAttempts: { length } }) => ({
    remainingAttempts: isUserTasked ? 'unlimited' : maxQuizAttempts - length
  }))
)(QuizAttemptText);

const useGooglePdfViewer = (link) => {
  const googlePdfViewerPrefix = 'https://docs.google.com/viewer?url=';
  const postfix = '&embedded=true';
  return `${googlePdfViewerPrefix}${encodeURIComponent(link)}${postfix}`;
};

const ContentQuiz = ({
  attemptingQuiz,
  displayView,
  earntMicroPoints,
  finishLoadingContent,
  isContentLoading,
  isLoadingQuizQuestions,
  onQuestionTypeChange,
  quizQuestions,
  onReturnToContent,
  selectedContent,
  setDisplayView,
  isFetchingCpdPoint,
  isFetchingQuizAttempts,
  cpdPoint,
  userSelectedCpdCategory,
  quizAttempts,
  maxQuizAttempts,
  onStartQuizAttempt,
  onEarnMicroCpdPoints,
  currentUser,
  isUserTasked,
  taskId,
  shouldBlockAds,

  // transcript state/handlers
  isTranscriptOpen,
  toggleTranscriptPane,
  transcriptPaneRatio,
  setSplitContainerEl,
  onTranscriptResizeMouseDown,
}) => {
  /**
   * Absolute transcript URL (http/https) when provided by the selected content.
   * Null when no transcript exists.
   *
   * @type {string|null}
   */
  const transcriptSourceUrl =
    selectedContent && selectedContent.contentTranscriptUrl
      ? toSafeExternalUrl(he.decode(selectedContent.contentTranscriptUrl))
      : null;

  /**
   * Absolute, http(s)-only URL used by the main content iframe. Null when the
   * stored link uses a dangerous scheme (for example javascript:).
   *
   * @type {string|null}
   */
  const contentSourceUrl = selectedContent
    ? toSafeExternalUrl(he.decode(selectedContent.link))
    : null;

  /**
   * True when this content supports transcript display.
   * Current constraint: transcript is only supported for URL-based content
   * (not for uploaded PDF content).
   *
   * @type {boolean}
   */
  const hasTranscript =
    !!selectedContent &&
    !selectedContent.pdfContentFileName &&
    !!transcriptSourceUrl;

  /**
   * True when transcript split UI should be rendered (pane open + transcript exists).
   *
   * @type {boolean}
   */
  const isTranscriptSplitOpen = hasTranscript && isTranscriptOpen;

  /**
   * CSS percentage value for transcript pane width (e.g. "50%").
   *
   * @type {string}
   */
  const transcriptPaneWidthCss = `${Math.round(transcriptPaneRatio * 100)}%`;

  const transcriptIsSupported =
    selectedContent &&
    transcriptSupportedContentTypes.includes(selectedContent.contentType);

  // Phone: hide toggle + transcript pane entirely
  const shouldRenderTranscriptUi = transcriptIsSupported;

  return (
    <div>
      {selectedContent ? (
        <div id='quiz'>
          <h4 className='flexbox center'>
            If you are having trouble accessing this content, you can find it{' '}
            <Link
              className='left-gap-sm text-green'
              to={contentSourceUrl || '#'}
              target='_blank'
            >
              here
            </Link>
            . Be sure to come back to Caddie to complete the quiz after you've
            consumed the content.
          </h4>

          <div
            id='quiz-content'
            className={classNames({
              hidden: displayView !== contentDisplayView.content
            })}
          >
            <div
              ref={setSplitContainerEl}
              className={classNames('contentSplit', {
                'contentSplit--open':
                  shouldRenderTranscriptUi && isTranscriptSplitOpen
              })}
              style={
                shouldRenderTranscriptUi && isTranscriptSplitOpen
                  ? { '--transcriptWidth': transcriptPaneWidthCss }
                  : null
              }
            >
              {/* MAIN CONTENT */}
              <div className='contentSplit__main'>
                <div className='contentSplit__media'>
                  {pdfSupport() || !selectedContent.pdfContentFileName ? (
                    selectedContent.pdfContentFileName ? (
                      <div className='googleViewer'>
                        <iframe
                          sandbox='allow-scripts allow-same-origin'
                          className={classNames('contentSplit__frame', {
                            shouldBlockAds
                          })}
                          onLoad={finishLoadingContent}
                          title={selectedContent.title}
                          src={contentSourceUrl ? useGooglePdfViewer(contentSourceUrl) : 'about:blank'}
                        />
                        <div
                          className='googleViewer__downloadMask'
                          aria-hidden='true'
                        />
                      </div>
                    ) : (
                      <iframe
                        sandbox='allow-same-origin allow-scripts allow-popups allow-forms'
                        className={classNames('contentSplit__frame', {
                          shouldBlockAds
                        })}
                        onLoad={finishLoadingContent}
                        title={selectedContent.title}
                        src={contentSourceUrl || 'about:blank'}
                      />
                    )
                  ) : (
                    <a
                      className='downloadBox'
                      href={contentSourceUrl || undefined}
                    >
                      <div className='innerBox'>
                        <p>To view this content, please download it here</p>
                        <figure>
                          <Icon icon='download' />
                        </figure>
                      </div>
                    </a>
                  )}
                </div>
              </div>

              {shouldRenderTranscriptUi && isTranscriptSplitOpen ? (
                <div className='contentSplit__right'>
                  <div
                    role='separator'
                    aria-orientation='vertical'
                    onMouseDown={onTranscriptResizeMouseDown}
                    title='Drag to resize'
                    className='contentSplit__handle'
                  />
                  <div className='contentSplit__transcript'>
                    <div className='googleViewer'>
                      <iframe
                        title={`${selectedContent.title} transcript`}
                        src={transcriptSourceUrl ? useGooglePdfViewer(transcriptSourceUrl) : 'about:blank'}
                        sandbox='allow-scripts allow-same-origin'
                        className='contentSplit__frame'
                      />
                      <div
                        className='googleViewer__downloadMask'
                        aria-hidden='true'
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {shouldRenderTranscriptUi ? (
                <div className='contentTranscriptBar contentTranscriptBar--belowNoShrink'>
                  {hasTranscript ? (
                    <Button
                      bsStyle='plain'
                      type='button'
                      onClick={toggleTranscriptPane}
                      className={classNames('transcriptToggle', {
                        'is-active': isTranscriptSplitOpen
                      })}
                    >
                      <Icon icon='document' />
                      <span className='transcriptToggle__arrow'>
                        {isTranscriptSplitOpen ? '◀' : '▶'}
                      </span>
                      <span className='transcriptToggle__label'>
                        Transcript
                      </span>
                    </Button>
                  ) : (
                    <Button
                      bsStyle='plain'
                      type='button'
                      disabled
                      className={classNames('transcriptToggle', 'is-disabled')}
                      title='This content does not have a transcript.'
                    >
                      <Icon icon='document' /> No transcript available
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {isContentLoading ? (
            <div
              id='quiz-loader'
              className={classNames({
                hidden: displayView !== contentDisplayView.content
              })}
            >
              <Spinner fadeIn='none' name='circle' className='spinner' />
              <br />
              <span>
                Your {fp.lowerCase(selectedContent.contentType)} is being loaded
                now...
              </span>
            </div>
          ) : null}

          {!quizQuestions.length && isLoadingQuizQuestions ? (
            <Loading />
          ) : (
            <QuizQuestion
              displayView={displayView}
              earntMicroPoints={earntMicroPoints}
              returnToContent={onReturnToContent}
              isUserTasked={isUserTasked}
              startQuiz={() => setDisplayView(contentDisplayView.questions)}
              taskId={parseInt(taskId, 10)}
            />
          )}
        </div>
      ) : (
        <Loading />
      )}

      <footer className='quizFooter'>
        {earntMicroPoints ||
        displayView === contentDisplayView.info ? null : attemptingQuiz ? (
          <div className='right'>
            <form>
              <ControlGroup
                control='radio'
                type='pill'
                className='no-margin'
                formItemProps={{ className: 'pill no-margin pull-right' }}
                label={null}
                onChange={onQuestionTypeChange}
              >
                <ControlOption
                  className='radio-content'
                  value={contentDisplayView.content}
                  checked={displayView === contentDisplayView.content}
                  type='radio'
                  label={<span>Content</span>}
                />
                <ControlOption
                  className='radio-question'
                  value={contentDisplayView.questions}
                  checked={displayView === contentDisplayView.questions}
                  type='radio'
                  label={<span>Questions</span>}
                />
              </ControlGroup>
            </form>
          </div>
        ) : (
          <div className='left'>
            <CpdPointStatus
              {...{
                selectedContent,
                quizQuestions,
                isFetchingCpdPoint,
                isFetchingQuizAttempts,
                cpdPoint,
                userSelectedCpdCategory,
                quizAttempts,
                maxQuizAttempts,
                onStartQuizAttempt,
                onEarnMicroCpdPoints,
                currentUser,
                isUserTasked
              }}
            />
          </div>
        )}
      </footer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  selectedContent: state.content.selectedContent,
  cpdPoint: state.content.selectedContent
    ? fp.find(
        (cpdPoint) => cpdPoint.contentId === state.content.selectedContent.id
      )(cpdPointsSelector(state))
    : null,
  isFetchingCpdPoint: state.cpdPoints.isFetching,
  contenetError: state.content.error,
  isLoadingQuizQuestions: state.quiz.questions.isFetching,
  quizQuestions: state.quiz.questions.result,
  quizAttempts: state.quiz.attempts.result,
  isFetchingQuizAttempts: state.quiz.attempts.isFetching,
  currentAttempt: currentAttemptSelector(state),
  maxQuizAttempts: maxQuizAttemptsSelector(state),
  taskedContents: state.task.taskedContents,
  userSelectedCpdCategory:
    state.content.selectedContent &&
    !state.cpdCategorys.isFetching &&
    !state.user.isFetchingCpdCategoryProgress
      ? fp.some((cpdCategory) =>
          fp.flow(
            fp.get('cpdCategorys'),
            fp.map('cpdCategoryId'),
            fp.includes(cpdCategory.id)
          )(state.content.selectedContent)
        )(
          getUserIndustryCpdCategorys(
            state.user.profile,
            state.industry.industriesWithCpdCategorys
          )
        )
      : null
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getContent: bindActionCreators(contentActions.content.getById, dispatch),
  getTaskedContents: bindActionCreators(
    taskActions.task.getTaskedContents,
    dispatch
  ),
  getQuestions: bindActionCreators(quizActions.quiz.questions.get, dispatch),
  getQuizAttempts: bindActionCreators(quizActions.quiz.attempts.get, dispatch),
  saveQuizAttempt: bindActionCreators(quizActions.quiz.attempts.save, dispatch),
  addMicroCpdPoints: bindActionCreators(
    cpdPointActions.cpdPoints.add,
    dispatch
  ),
  getCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.get, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    () => ({
      displayView: contentDisplayView.content,
      attemptingQuiz: false,
      earntMicroPoints: false,
      isContentLoading: true,

       /**
       * Whether the transcript pane is currently open.
       * Note: transcript pane will only render if transcript exists.
       */
      isTranscriptOpen: true,
      
      /**
       * Transcript pane width as a ratio of the split container width.
       * Example: 0.5 means transcript is 50% of the available width.
       */
      transcriptPaneRatio: DEFAULT_TRANSCRIPT_PANE_RATIO,
      
      /**
       * The split container DOM element used for measuring during drag resizing.
       *
       * @type {HTMLElement|null}
       */
      splitContainerEl: null
    }),
    {
      finishLoadingContent: () => () => ({ isContentLoading: false }),
      setDisplayView: () => (displayView) => ({ displayView }),
      returnToContent: () => () => ({
        displayView: contentDisplayView.content,
        attemptingQuiz: false,
        earntMicroPoints: false,
        isTranscriptOpen: false
      }),
      startQuizAttempt: () => () => ({
        displayView: contentDisplayView.info,
        attemptingQuiz: true
      }),
      earnMicroCpdPoints: () => () => ({
        displayView: contentDisplayView.questions,
        earntMicroPoints: true
      }),

      /**
       * Stores the split container DOM element for later measurement.
       *
       * @returns {(node: HTMLElement|null) => { splitContainerEl: HTMLElement|null }}
       */
      setSplitContainerEl: () => (node) => ({ splitContainerEl: node }),

      /**
       * Updates the transcript pane width ratio.
       *
       * @returns {(ratio: number) => { transcriptPaneRatio: number }}
       */
      setTranscriptPaneRatio: () => (ratio) => ({ transcriptPaneRatio: ratio }),

      /**
       * Toggles transcript pane open/closed.
       * When opening, ensures the pane starts at the default ratio.
       */
      toggleTranscriptPane:
        ({ isTranscriptOpen, transcriptPaneRatio }) =>
        () => ({
          isTranscriptOpen: !isTranscriptOpen,
          transcriptPaneRatio: isTranscriptOpen
            ? transcriptPaneRatio
            : DEFAULT_TRANSCRIPT_PANE_RATIO
        })
    }
  ),
  withHandlers({
    onQuestionTypeChange:
      ({ setDisplayView }) =>
      (e) =>
        setDisplayView(e.target.value),

    onReturnToContent:
      ({ getCpdPoints, returnToContent, selectedContent: { id: contentId } }) =>
      () => {
        getCpdPoints({ skip: 0, contentId });
        returnToContent();
      },

    onStartQuizAttempt:
      ({
        saveQuizAttempt,
        selectedContent: { id: contentId },
        startQuizAttempt
      }) =>
      () => {
        startQuizAttempt();
        saveQuizAttempt({ contentId });
      },

    onEarnMicroCpdPoints:
      ({ addMicroCpdPoints, earnMicroCpdPoints, selectedContent: { id } }) =>
      () => {
        earnMicroCpdPoints();
        addMicroCpdPoints(id);
      },

    /**
     * Mouse down handler for the transcript resize separator.
     * Registers mousemove/mouseup listeners on window to allow dragging outside the handle.
     *
     * Computes a transcript pane ratio based on cursor position relative to the split container:
     * - Separator represents the left edge of the transcript pane
     * - Transcript width = container width - cursorXWithinContainer
     */
    onTranscriptResizeMouseDown:
      ({ splitContainerEl, setTranscriptPaneRatio }) =>
      (e) => {
        if (!splitContainerEl) return;

        e.preventDefault();
        e.stopPropagation();

        document.body.classList.add('is-split-dragging');
        splitContainerEl.classList.add('is-resizing');

        const startRect = splitContainerEl.getBoundingClientRect();

        const onMove = (moveEvent) => {
          const xWithinContainer = moveEvent.clientX - startRect.left;
          const containerWidth = startRect.width;

          // Separator is at the left edge of transcript pane.
          const transcriptPaneWidth = containerWidth - xWithinContainer;
          let nextRatio = transcriptPaneWidth / containerWidth;

          // Enforce a minimum width in pixels AND as a ratio.
          const minRatioFromPx = MIN_TRANSCRIPT_PANE_PX / containerWidth;
          const minRatio = Math.max(MIN_TRANSCRIPT_PANE_RATIO, minRatioFromPx);

          nextRatio = clampNumber(
            nextRatio,
            minRatio,
            MAX_TRANSCRIPT_PANE_RATIO
          );

          setTranscriptPaneRatio(nextRatio);
        };

        const onUp = () => {
          document.body.classList.remove('is-split-dragging');
          splitContainerEl.classList.remove('is-resizing');
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      }
  }),
  withProps(({ location: { search }, match: { params } }) => ({
    contentId: parseInt(
      queryString.parse(search).contentId || params.contentId,
      10
    )
  })),
  withProps(({ contentId, taskedContents, selectedContent }) => ({
    isUserTasked: fp.contains(contentId)(taskedContents),
    shouldBlockAds: fp.any((adsBlockUrl) =>
      fp.includes(adsBlockUrl)(fp.get('link')(selectedContent))
    )(adsBlockList)
  })),
  withProps(({ location: { search } }) => ({
    taskId: queryString.parse(search).taskId
  })),
  lifecycle({
    componentDidMount() {
      const {
        finishLoadingContent,
        getContent,
        getCpdPoints,
        getQuestions,
        getQuizAttempts,
        getTaskedContents,
        goTo,
        contentId,
        getPublishers,
      } = this.props;

      if (contentId) {
        getPublishers();
        getTaskedContents();
        getQuizAttempts(contentId);
        getQuestions(contentId);
        getCpdPoints({ skip: 0, contentId });
        getContent(contentId);
      } else {
        goTo(routes.app.training.home);
      }

      // We still remove the spinner after reasonable amount of time, in case iFrame onload callback failed to fire due to browser compatibility or other reasons.
      // Even our spinner is well hidden under the iFrame page, but it won't be well hidden if the iFrame page's background is transparent.
      this.contentLoadingTimeout = setTimeout(
        finishLoadingContent,
        CONTENT_LOADING_TIMEOUT_IN_SECONDS
      );
    },
    componentWillReceiveProps(nextProps) {
      const { attemptingQuiz, goTo } = this.props;

      if (nextProps.contentError) {
        goTo(routes.app.training.home);
      } else if (nextProps.currentAttempt && !attemptingQuiz) {
        this.setState({ attemptingQuiz: true });
      }
    },
    componentWillUnmount() {
      clearTimeout(this.contentLoadingTimeout);
    }
  })
)(ContentQuiz);



// WEBPACK FOOTER //
// ./src/containers/pages/app/training/content-quiz.js